import { useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { matchPath, useLocation } from 'react-router-dom-v5';
import { Link } from 'react-router-dom-v5';
import { ActionCableConsumer } from 'react-actioncable-provider';
import { Bell, BellDot } from 'lucide-react';
import { cn } from '@/shared/utils/cn';
import {
  SidebarMenuButton,
  SidebarMenuItem,
} from '@/shared/ui/sidebar';
import { getUnreadNotificationsCount } from '../../selectors/notificationsUserSelectors';
import {
  fetchNotificationsUsers,
  notificationsUserFetched,
} from '../../processes/notificationsUserProcesses';

const unreadBadgeStyle: React.CSSProperties = {
  backgroundColor: '#0284C7',
  color: '#fff',
};

/**
 * Sidebar entry for "Notificações".
 *
 * Keeps the ActionCable subscription alive and fetches notification
 * data into Redux on mount. Uses BellDot icon when there are unread
 * notifications (matching the Figma design), Bell otherwise.
 */
export function NotificationsNavItem({ label }: { label: string }) {
  const { pathname } = useLocation();
  const dispatch = useDispatch();
  const unreadCount = useSelector(
    (s: unknown) => getUnreadNotificationsCount(s as never) as number,
  );

  useEffect(() => {
    fetchNotificationsUsers(dispatch);
  }, [dispatch]);

  const onReceived = useCallback(
    (payload: { data?: unknown }) => {
      notificationsUserFetched(dispatch, payload?.data);
    },
    [dispatch],
  );

  const isActive = !!matchPath(pathname, {
    path: '/notifications',
    exact: true,
  });

  const Icon = unreadCount > 0 ? BellDot : Bell;

  return (
    <SidebarMenuItem>
      <ActionCableConsumer
        channel={{ channel: 'NotificationsChannel' }}
        onReceived={onReceived}
      />
      <SidebarMenuButton
        asChild
        isActive={isActive}
        tooltip={label}
        className={cn(isActive && 'font-medium')}
      >
        <Link to="/notifications">
          <Icon />
          <span>{label}</span>
          {unreadCount > 0 && (
            <span
              className="ml-1 inline-flex min-w-[18px] h-[18px] items-center justify-center rounded-full px-1 text-[10px] font-semibold"
              style={unreadBadgeStyle}
            >
              {unreadCount > 99 ? '99+' : unreadCount}
            </span>
          )}
        </Link>
      </SidebarMenuButton>
    </SidebarMenuItem>
  );
}
